import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { LessonLog, ProgressItem, Student, TestRecord } from '@/types';
import Field, { inputClass } from '@/components/base/Field';
import { markProgressDone, markProgressPlanned } from '@/lib/progress';
import TestRecordEditor, { newTestEntry, type TestEntry } from './TestRecordEditor';
import PreviousLessonPanel, { type PreviousLessonPatch } from './PreviousLessonPanel';
import TemplateTextarea from './TemplateTextarea';
import { useLessonTemplates } from '@/hooks/useLessonTemplates';
import { fetchOverrideContext } from '@/lib/lessonOverrides';
import { formatMonthDay } from '@/lib/datetime';
import type { LessonOverride } from '@/types';

interface LessonFormProps {
  lessonId?: string;
  fixedStudentId?: string;
  initialLessonDate?: string;
  initialIsMakeup?: boolean;
  makeupScheduleId?: string;
  onSaved: (id: string) => void;
  onCancel: () => void;
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function LessonForm({
  lessonId,
  fixedStudentId,
  initialLessonDate,
  initialIsMakeup,
  makeupScheduleId,
  onSaved,
  onCancel,
}: LessonFormProps) {
  const [students, setStudents] = useState<Student[]>([]);
  const [studentId, setStudentId] = useState(fixedStudentId ?? '');
  const [lessonDate, setLessonDate] = useState(initialLessonDate ?? todayStr());
  const [isAbsent, setIsAbsent] = useState(false);
  const [isMakeup, setIsMakeup] = useState(Boolean(initialIsMakeup));
  const [makeUpDate, setMakeUpDate] = useState('');
  const [lessonScope, setLessonScope] = useState('');
  const [nextHomework, setNextHomework] = useState('');
  const [homeworkCheck, setHomeworkCheck] = useState('');
  const [notes, setNotes] = useState('');
  const [tests, setTests] = useState<TestEntry[]>([]);
  const [progressItems, setProgressItems] = useState<ProgressItem[]>([]);
  const [linkedProgressIds, setLinkedProgressIds] = useState<string[]>([]);
  const [originalProgressIds, setOriginalProgressIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(Boolean(lessonId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [dayOverride, setDayOverride] = useState<LessonOverride | null>(null);
  const [movedIn, setMovedIn] = useState<LessonOverride[]>([]);
  const { byField, addTemplate, removeTemplate } = useLessonTemplates();

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('students')
          .select('*')
          .order('name', { ascending: true });
        if (fetchError) throw fetchError;
        if (active) setStudents((data as Student[]) ?? []);
      } catch {
        if (active) setError('학생 목록을 불러오지 못했어요.');
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const loadLesson = useCallback(async () => {
    if (!lessonId) return;
    setLoading(true);
    setError('');
    try {
      const [logRes, testRes] = await Promise.all([
        supabase.from('lesson_logs').select('*').eq('id', lessonId).maybeSingle(),
        supabase
          .from('test_records')
          .select('*')
          .eq('lesson_log_id', lessonId)
          .order('created_at', { ascending: true }),
      ]);
      if (logRes.error) throw logRes.error;
      const log = logRes.data as LessonLog | null;
      if (log) {
        setStudentId(log.student_id);
        setLessonDate(log.lesson_date ?? '');
        setIsAbsent(Boolean(log.is_absent));
        setIsMakeup(Boolean(log.is_makeup));
        setMakeUpDate(log.make_up_date ?? '');
        setLessonScope(log.lesson_scope ?? '');
        setNextHomework(log.next_homework ?? '');
        setHomeworkCheck(log.homework_check ?? '');
        setNotes(log.notes ?? '');
        const linked = log.progress_item_ids ?? [];
        setLinkedProgressIds(linked);
        setOriginalProgressIds(linked);
      }
      setTests(
        ((testRes.data as TestRecord[]) ?? []).map((t) => ({
          ...newTestEntry(),
          scope: t.test_scope ?? '',
          difficulty: t.difficulty ?? '중',
          score: t.score ?? '',
        })),
      );
    } catch {
      setError('수업일지를 불러오지 못했어요.');
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    loadLesson();
  }, [loadLesson]);

  // 선택한 학생·날짜의 수업 일정 변경(시간 변경/날짜 변경/휴강) 정보를 보여줘요.
  useEffect(() => {
    let active = true;
    if (!studentId || !lessonDate) {
      setDayOverride(null);
      setMovedIn([]);
      return undefined;
    }
    (async () => {
      try {
        const ctx = await fetchOverrideContext(studentId, lessonDate);
        if (!active) return;
        setDayOverride(ctx.out);
        setMovedIn(ctx.movedIn);
      } catch {
        if (active) {
          setDayOverride(null);
          setMovedIn([]);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [studentId, lessonDate]);

  // 선택한 학생의 진도 단원을 불러와요.
  useEffect(() => {
    if (!studentId) {
      setProgressItems([]);
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from('progress_items')
          .select('*')
          .eq('student_id', studentId)
          .order('created_at', { ascending: true });
        if (fetchError) throw fetchError;
        if (active) setProgressItems((data as ProgressItem[]) ?? []);
      } catch {
        if (active) setProgressItems([]);
      }
    })();
    return () => {
      active = false;
    };
  }, [studentId]);

  // 학생을 바꾸면(새 수업일지) 진도 선택을 초기화해요.
  useEffect(() => {
    if (lessonId) return;
    setLinkedProgressIds([]);
    setOriginalProgressIds([]);
  }, [studentId, lessonId]);

  const toggleProgress = (id: string) => {
    setLinkedProgressIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  // 기존 내용이 있으면 줄바꿈으로 이어 붙이고, 없으면 그대로 채워요.
  const mergeText = (current: string, incoming: string): string => {
    const value = incoming.trim();
    if (!value) return current;
    return current.trim() ? `${current.trimEnd()}\n${value}` : value;
  };

  // 지난 수업일지에서 고른 항목을 지금 입력칸에 채워요.
  const applyPreviousLesson = (patch: PreviousLessonPatch) => {
    if (patch.lessonScope !== undefined) {
      setLessonScope((prev) => mergeText(prev, patch.lessonScope ?? ''));
    }
    if (patch.nextHomework !== undefined) {
      setNextHomework((prev) => mergeText(prev, patch.nextHomework ?? ''));
    }
    if (patch.homeworkCheck !== undefined) {
      setHomeworkCheck((prev) => mergeText(prev, patch.homeworkCheck ?? ''));
    }
    if (patch.notes !== undefined) {
      setNotes((prev) => mergeText(prev, patch.notes ?? ''));
    }
    if (patch.testScopes && patch.testScopes.length > 0) {
      setTests((prev) => {
        const existing = new Set(prev.map((t) => t.scope.trim()).filter(Boolean));
        const additions = patch.testScopes!
          .map((s) => s.trim())
          .filter((s) => s && !existing.has(s))
          .map((s) => ({ ...newTestEntry(), scope: s }));
        return additions.length > 0 ? [...prev, ...additions] : prev;
      });
    }
  };

  const selectableProgress = useMemo(
    () =>
      progressItems.filter(
        (p) => p.status === 'planned' || originalProgressIds.includes(p.id),
      ),
    [progressItems, originalProgressIds],
  );

  const progressGroups = useMemo(() => {
    const map = new Map<string, ProgressItem[]>();
    selectableProgress.forEach((it) => {
      if (!map.has(it.category)) map.set(it.category, []);
      map.get(it.category)!.push(it);
    });
    return Array.from(map.entries());
  }, [selectableProgress]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!studentId) {
      setError('학생을 선택해주세요.');
      return;
    }
    if (!lessonDate) {
      setError('수업 일자를 선택해주세요.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        student_id: studentId,
        lesson_date: lessonDate,
        is_absent: isAbsent,
        is_makeup: isMakeup,
        make_up_date: isAbsent && makeUpDate ? makeUpDate : null,
        lesson_scope: lessonScope.trim(),
        next_homework: nextHomework.trim(),
        homework_check: homeworkCheck.trim(),
        notes: notes.trim(),
        progress_item_ids: linkedProgressIds,
      };

      let savedId = lessonId ?? '';
      if (lessonId) {
        const { error: updateError } = await supabase
          .from('lesson_logs')
          .update(payload)
          .eq('id', lessonId);
        if (updateError) throw updateError;
        const { error: clearError } = await supabase
          .from('test_records')
          .delete()
          .eq('lesson_log_id', lessonId);
        if (clearError) throw clearError;
      } else {
        const { data, error: insertError } = await supabase
          .from('lesson_logs')
          .insert(payload)
          .select();
        if (insertError) throw insertError;
        savedId = (data as { id: string }[] | null)?.[0]?.id ?? '';
      }

      const validTests = tests.filter((t) => t.scope.trim() || t.score.trim());
      if (savedId && validTests.length > 0) {
        const { error: testError } = await supabase.from('test_records').insert(
          validTests.map((t) => ({
            lesson_log_id: savedId,
            student_id: studentId,
            test_scope: t.scope.trim(),
            difficulty: t.difficulty || '중',
            score: t.score.trim(),
          })),
        );
        if (testError) throw testError;
      }

      if (makeupScheduleId && savedId) {
        const { error: linkError } = await supabase
          .from('makeup_schedules')
          .update({ lesson_log_id: savedId })
          .eq('id', makeupScheduleId);
        if (linkError) throw linkError;
      }

      // 진도 연동: 선택한 단원은 완료 처리, 해제한 단원은 예정으로 되돌려요.
      const remaining = originalProgressIds.filter((pid) => !linkedProgressIds.includes(pid));
      await markProgressDone(linkedProgressIds);
      await markProgressPlanned(remaining);

      onSaved(savedId);
    } catch {
      setError('수업일지 저장에 실패했어요. 잠시 후 다시 시도해주세요.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-foreground-500">
        <i className="ri-loader-4-line mr-2 animate-spin text-xl"></i>
        불러오는 중...
      </div>
    );
  }

  const fixedStudentName = students.find((s) => s.id === fixedStudentId)?.name;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6">
      <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
        <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
            <i className="ri-calendar-check-line"></i>
          </span>
          수업 기본 정보
        </h2>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="학생" required>
            {fixedStudentId ? (
              <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 py-2 text-sm text-foreground-800">
                <i className="ri-user-3-line text-primary-600"></i>
                {fixedStudentName || '선택된 학생'}
              </div>
            ) : (
              <select
                value={studentId}
                onChange={(e) => setStudentId(e.target.value)}
                className={inputClass}
              >
                <option value="">학생을 선택하세요</option>
                {students.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.school ? ` · ${s.school}` : ''}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="수업 일자" required>
            <input
              type="date"
              value={lessonDate}
              onChange={(e) => setLessonDate(e.target.value)}
              className={inputClass}
            />
          </Field>
        </div>

        {dayOverride?.kind === 'time' && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-accent-200 bg-accent-50 px-3.5 py-2.5">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-equalizer-line text-sm"></i>
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-accent-800">이 날짜는 변경된 수업시간이 있어요</p>
              <p className="mt-0.5 text-sm font-bold text-foreground-900">
                {dayOverride.start_time}
                {dayOverride.end_time ? ` ~ ${dayOverride.end_time}` : ''}
              </p>
              {dayOverride.note && (
                <p className="mt-0.5 text-xs text-foreground-600">{dayOverride.note}</p>
              )}
            </div>
          </div>
        )}

        {dayOverride?.kind === 'move' && dayOverride.moved_to_date && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3.5 py-2.5">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-calendar-2-line text-sm"></i>
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-primary-800">
                이 날 수업은 {formatMonthDay(dayOverride.moved_to_date)}로 이동했어요
              </p>
              {(dayOverride.start_time || dayOverride.end_time) && (
                <p className="mt-0.5 text-sm font-bold text-foreground-900">
                  {dayOverride.start_time}
                  {dayOverride.end_time ? ` ~ ${dayOverride.end_time}` : ''}
                </p>
              )}
              {dayOverride.note && (
                <p className="mt-0.5 text-xs text-foreground-600">{dayOverride.note}</p>
              )}
            </div>
          </div>
        )}

        {dayOverride?.kind === 'cancel' && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-background-300 bg-background-100 px-3.5 py-2.5">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-background-200 text-foreground-600">
              <i className="ri-user-unfollow-line text-sm"></i>
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-foreground-800">이 날 수업은 휴강이에요</p>
              {dayOverride.note && (
                <p className="mt-0.5 text-xs text-foreground-600">{dayOverride.note}</p>
              )}
            </div>
          </div>
        )}

        {movedIn.length > 0 && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 px-3.5 py-2.5">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className="ri-calendar-2-line text-sm"></i>
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-primary-800">
                이 날짜로 옮겨온(일정 변경된) 수업이 있어요
              </p>
              {movedIn.map((o) => (
                <p key={o.id} className="mt-0.5 text-sm font-bold text-foreground-900">
                  {formatMonthDay(o.override_date)} 수업 →{' '}
                  {o.start_time || '정규 시간'}
                  {o.end_time ? ` ~ ${o.end_time}` : ''}
                </p>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4">
          <p className="mb-2 text-sm font-medium text-foreground-800">수업 구분</p>
          <div className="inline-flex gap-1 rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setIsMakeup(false)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                !isMakeup
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className="ri-book-2-line"></i>
              정규 수업
            </button>
            <button
              type="button"
              onClick={() => setIsMakeup(true)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                isMakeup
                  ? 'bg-accent-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className="ri-calendar-schedule-line"></i>
              보충 수업
            </button>
          </div>
          {isMakeup && (
            <p className="mt-2 flex items-center gap-1 text-xs text-accent-700">
              <i className="ri-information-line"></i>
              보충 수업으로 기록돼요. 학생카드의 보충 일정(추가 수업)과 함께 표시돼요.
            </p>
          )}
        </div>

        <div className="mt-4">
          <p className="mb-2 text-sm font-medium text-foreground-800">출결</p>
          <div className="inline-flex gap-1 rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setIsAbsent(false)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                !isAbsent
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className="ri-check-line"></i>
              출석
            </button>
            <button
              type="button"
              onClick={() => setIsAbsent(true)}
              className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                isAbsent
                  ? 'bg-accent-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              <i className="ri-user-unfollow-line"></i>
              결석
            </button>
          </div>
        </div>

        {isAbsent && (
          <div className="mt-4 sm:max-w-xs">
            <Field label="보충 예정일">
              <input
                type="date"
                value={makeUpDate}
                onChange={(e) => setMakeUpDate(e.target.value)}
                className={inputClass}
              />
            </Field>
          </div>
        )}
      </section>

      <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
        <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
            <i className="ri-book-2-line"></i>
          </span>
          수업 기록
        </h2>
        {studentId && (
          <div className="mt-4">
            <PreviousLessonPanel
              studentId={studentId}
              lessonId={lessonId}
              onApply={applyPreviousLesson}
            />
          </div>
        )}
        <div className="mt-5 flex flex-col gap-4">
          <TemplateTextarea
            label="수업 범위"
            field="lesson_scope"
            value={lessonScope}
            onChange={setLessonScope}
            templates={byField.lesson_scope ?? []}
            onSaveTemplate={addTemplate}
            onDeleteTemplate={removeTemplate}
            rows={3}
            placeholder="예: 개념원리 공통수학1 - 이차방정식의 판별식 ~ 근과 계수의 관계"
          />
          <TemplateTextarea
            label="다음 수업 숙제"
            field="next_homework"
            value={nextHomework}
            onChange={setNextHomework}
            templates={byField.next_homework ?? []}
            onSaveTemplate={addTemplate}
            onDeleteTemplate={removeTemplate}
            rows={3}
            placeholder="예: 쎈 유형서 p.120 ~ p.135, 오답노트 3문제"
          />
          <TemplateTextarea
            label="숙제 이행검사"
            field="homework_check"
            value={homeworkCheck}
            onChange={setHomeworkCheck}
            templates={byField.homework_check ?? []}
            onSaveTemplate={addTemplate}
            onDeleteTemplate={removeTemplate}
            rows={2}
            placeholder="예: 지난 숙제 전 범위 완료, 오답 2문제 재풀이 필요"
          />
          <TemplateTextarea
            label="특이사항"
            field="notes"
            value={notes}
            onChange={setNotes}
            templates={byField.notes ?? []}
            onSaveTemplate={addTemplate}
            onDeleteTemplate={removeTemplate}
            rows={2}
            placeholder="예: 컨디션 좋지 않음, 다음 시간 진도 조절 예정"
          />
        </div>
      </section>

      {studentId && (
        <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-base font-bold text-foreground-950">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className="ri-route-line"></i>
              </span>
              진도 연동
            </h2>
            {selectableProgress.length > 0 && (
              <span className="text-xs text-foreground-500">
                선택 <strong className="font-semibold text-primary-700">{linkedProgressIds.length}</strong>개
              </span>
            )}
          </div>
          <p className="mt-1.5 text-xs text-foreground-500">
            이번 수업에서 끝낸 단원을 체크하면 학생카드 &lsquo;남은 진도&rsquo;에 자동으로 완료
            처리돼요.
          </p>

          {selectableProgress.length === 0 ? (
            <p className="mt-4 rounded-lg border border-dashed border-background-300 py-8 text-center text-xs text-foreground-500">
              연동할 진도 단원이 없어요. 학생카드 &lsquo;남은 진도&rsquo; 탭에서 단원을 먼저
              등록해주세요.
            </p>
          ) : (
            <div className="mt-4 flex flex-col gap-3">
              {progressGroups.map(([category, groupItems]) => (
                <div
                  key={category}
                  className="rounded-lg border border-background-200 bg-background-100 p-3.5"
                >
                  <p className="text-xs font-bold text-foreground-800">{category}</p>
                  <div className="mt-2.5 flex flex-wrap gap-2">
                    {groupItems.map((item) => {
                      const active = linkedProgressIds.includes(item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => toggleProgress(item.id)}
                          className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition whitespace-nowrap ${
                            active
                              ? 'border-primary-400 bg-primary-100 text-primary-700'
                              : 'border-background-300 bg-background-50 text-foreground-600 hover:bg-background-200'
                          }`}
                        >
                          <i
                            className={
                              active ? 'ri-checkbox-circle-fill' : 'ri-add-circle-line'
                            }
                          ></i>
                          {item.unit}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-6">
        <h2 className="mb-4 flex items-center gap-2 text-base font-bold text-foreground-950">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
            <i className="ri-quill-pen-line"></i>
          </span>
          테스트
        </h2>
        <TestRecordEditor tests={tests} onChange={setTests} />
      </section>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-accent-200 bg-accent-100/70 px-4 py-2.5 text-sm text-accent-800">
          <i className="ri-error-warning-line"></i>
          {error}
        </div>
      )}

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 whitespace-nowrap"
        >
          취소
        </button>
        <button
          type="submit"
          disabled={saving}
          className="flex items-center justify-center gap-1.5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-50 whitespace-nowrap"
        >
          {saving ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              저장 중...
            </>
          ) : (
            <>
              <i className="ri-check-line"></i>
              {lessonId ? '수정 저장' : '수업일지 저장'}
            </>
          )}
        </button>
      </div>
    </form>
  );
}